import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Router, Event, NavigationStart } from '@angular/router';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { LoginService } from '@core/auth/login.service';
import { AdminMeStoreService } from '@core/domains/adminMe/adminMe-store.service';
import { AdminMe } from '@core/domains/adminMe/admin-me';
import { TranslocoService, TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { Observable, of, OperatorFunction, Subscription } from 'rxjs';
import { NavigationBranch, NavigationItem, NavigationTree, getFullNavTree } from './navigation-tree/navigation-tree.constant';
import { tap } from 'rxjs/internal/operators/tap';
import { map } from 'rxjs/internal/operators/map';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter } from 'rxjs/internal/operators/filter';
import { mergeMap } from 'rxjs/internal/operators/mergeMap';
import { Environment } from '@core/domains/environment/environment';
import { EnvironmentService } from '@core/domains/environment/environment.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    providers: [{ provide: TRANSLOCO_SCOPE, useValue: { scope: 'layout' } }],
    standalone: false
})
export class LayoutComponent implements OnInit {
  private _subscription: Subscription;
  private _destroyRef: DestroyRef = inject(DestroyRef);

  public adminMe: AdminMe;
  public opened = true;
  public handset: boolean;
  public version: Environment['version'];
  public copyrightYear: string = new Date().getFullYear().toString();
  public trees: NavigationTree;

  constructor(
    private loginService: LoginService,
    private adminStore: AdminMeStoreService,
    private router: Router,
    private snack: MatSnackBar,
    private loco: TranslocoService,
    private breakpoint: BreakpointObserver,
    private envService: EnvironmentService,
  ) {

    this.breakpoint.observe(['(max-width: 980px)']).pipe(
      tap((state: BreakpointState) => {
        this.handset = state.matches;
        this.opened = !state.matches;
        this._subscription?.unsubscribe();
        if (this.handset) {
          this._subscription = this.router.events.pipe(
            filter((event: Event) => event instanceof NavigationStart),
            tap(() => this.opened = false)
          ).subscribe();
        }
      }),
      takeUntilDestroyed(this._destroyRef)
    ).subscribe();
  }

  ngOnInit(): void {
    this.fetchAdminInfos().pipe(
      mergeMap(() => this.fetchNavTree())
    ).subscribe({next: () => this.version = this.envService.environment.version});
  }

  redirect(): void {
    this.router.navigate(['/']);
  }

  logout (): void {
    this.loginService.onLogout();
  }

  fetchAdminInfos(): Observable<AdminMe> {
    if (!this.adminStore.adminMe) {
      this.adminStore.initAdmin();
    }
    return this.adminStore.$adminMe.pipe(
      tap((me: AdminMe) => this.adminMe = me),
    );
  }

  fetchNavTree(): Observable<NavigationTree> {
    return getFullNavTree().pipe(
      this.filterByProduct(),
      this.filterByTreeType(),
      tap((result: NavigationTree) => this.trees = result)
    );
  }

  /**
   * Custom RXJS operator, filter nav-tree items by products
   */
  filterByProduct(): OperatorFunction<NavigationTree, NavigationTree> {
    return (source: Observable<NavigationTree>): Observable<NavigationTree> => {
      return source.pipe(
        map((tree: NavigationTree) => tree.map((branch: NavigationBranch) => {
          return branch.filter((item: NavigationItem) => this._checkForProduct(item.products)).map((item: NavigationItem) => {
            return item.children ? Object.assign({}, item, {children: item.children.filter((child: NavigationItem) => this._checkForProduct(child.products))}) : item;
          })
        }))
      );
    };
  }

  /**
   * Custom RXJS operator, filter nav-tree items by type
   */
  filterByTreeType(): OperatorFunction<NavigationTree, NavigationTree> {
    return (source: Observable<NavigationTree>): Observable<NavigationTree> => {
      return source.pipe(
        map((fullTree: NavigationTree) => fullTree.filter((branch: NavigationBranch) => branch.filter((item) => item.onMenu)))
      );
    };
  }

  errorManagement(): Observable<never> {
    this.snack.open(this.loco.translate('API_ERRORS.common'), undefined, {panelClass: 'mat-snack-bar-error'});
    this.logout();
    return of();
  }

  private _checkForProduct(products: string[]): boolean {
    return products.some((val: string) => this.adminMe.products.includes(val));
  }
}
